import { ErrorMessage, TaAutocomplete } from '@components/shared';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import { FormControlLabel, FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary } from '@utils/util.types';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrops, useCropsStore } from 'services/crops';
import { useLanguages, useLanguagesStore } from 'services/languages';
import { useUpdateUser } from 'services/users';
import * as Yup from 'yup';
import '../styles.css';

interface ForErrorTextProps {
	error?: string | boolean;
	fieldName?: string;
}

const helperTextStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: '9px',
	marginBottom: '4px',
	color: '#FD3333',
};

interface FarmerPreferencesFormValues {
	userLanguages: Dictionary[];
	crops: Dictionary[];
	user_agreed_term_and_condition: boolean;
}

export function ForErrorText({ error, fieldName }: ForErrorTextProps) {
	const { t } = useTranslation();

	if (!error) return null;

	return (
		<FormHelperText sx={helperTextStyles}>
			<ErrorOutlineOutlinedIcon />
			{typeof error === 'string' ? t(error, { fieldName }) : ''}
		</FormHelperText>
	);
}

const personalDetailsValidationSchema = Yup.object().shape({
	userLanguages: Yup.array().min(1, 'fieldNameError'),
	crops: Yup.array().min(1, 'fieldNameError'),
	user_agreed_term_and_condition: Yup.bool().oneOf([true], 'userAgreementError'),
});
const initialValues = {
	userLanguages: [],
	crops: [],
	user_agreed_term_and_condition: false,
};

export function FarmerPreferencesNew() {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { crops } = useCropsStore();
	const { languages } = useLanguagesStore();
	const [isLoading, setIsLoading] = useState(false);

	const mutate = useUpdateUser();

	useCrops();
	useLanguages();

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);
	const languagesDictionary = useMemo(() => mapToDictionary(languages), [languages]);

	const formik = useFormik<FarmerPreferencesFormValues>({
		initialValues: initialValues,
		validationSchema: personalDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsLoading(true);
				await mutate({
					userLanguages: values.userLanguages.map((language) => language.id),
					crops: values.crops.map((crop) => crop.id),
					user_completed_registration: true,
					user_agreed_term_and_condition: values.user_agreed_term_and_condition,
				});

				navigateWithLanguage('/farmer/dashboard', {
					replace: true,
				});
			} catch (error) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const renderAutocomplete = (
		label: string,
		name: keyof FarmerPreferencesFormValues,
		options: Dictionary[]
	) => {
		return (
			<div>
				<TaAutocomplete
					required
					options={options}
					getOptionLabel={(option) => option.name}
					label={t(label)}
					icon={
						name === 'crops' ? (
							<SpaOutlinedIcon
								color={formik.touched[name] && formik.errors[name] ? 'error' : 'primary'}
							/>
						) : (
							<RecordVoiceOverOutlinedIcon
								color={formik.touched[name] && formik.errors[name] ? 'error' : 'primary'}
							/>
						)
					}
					multiple
					filterSelectedOptions
					value={formik.values[name] as Dictionary[]}
					onChange={(event, value) => formik.setFieldValue(name, value)}
					error={Boolean(formik.touched[name] && formik.errors[name])}
				/>
				{formik.touched[name] && formik.errors[name] && (
					<ErrorMessage message={t(formik.errors[name] as string, { fieldName: t(label) })} />
				)}
			</div>
		);
	};

	return (
		<>
			<p className="text-base text-dark font-bold uppercase">{t('preferences')}</p>
			<form onSubmit={formik.handleSubmit} className="mt-[20px] flex flex-col gap-[20px] pb-5">
				{/* Communication Language Autocomplete */}
				{renderAutocomplete('communicationLanguage', 'userLanguages', languagesDictionary)}

				{/* Crops Autocomplete */}
				{renderAutocomplete('selectCrops', 'crops', cropsDictionary)}
				<div>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.user_agreed_term_and_condition}
								onChange={formik.handleChange}
								name="user_agreed_term_and_condition"
								sx={{
									paddingTop: 0,

									'& .MuiSvgIcon-root': {
										color:
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition
												? '#FD3333'
												: '#00AB89',
									},
								}}
							/>
						}
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
						}}
						label={
							<span
								className={classNames('agreement-grey-text', {
									'!text-error':
										formik.touched.user_agreed_term_and_condition &&
										formik.errors.user_agreed_term_and_condition,
								})}
							>
								{t('agree')}{' '}
								<a
									href="https://telagri.com/terms-conditions"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('termsAndConditions')}
								</a>{' '}
								<a
									href="https://telagri.com/privacy-policy"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('privacyPolicy')}
								</a>{' '}
								<span
									className={classNames('agreement-grey-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('and')}
								</span>{' '}
								<a
									href="https://telagri.com/specific-terms"
									target="_blank"
									className={classNames('agreement-green-text', {
										'!text-error':
											formik.touched.user_agreed_term_and_condition &&
											formik.errors.user_agreed_term_and_condition,
									})}
								>
									{t('specificTerms')}
								</a>
							</span>
						}
					/>
					{formik.touched.user_agreed_term_and_condition &&
						formik.errors.user_agreed_term_and_condition && (
							<ErrorMessage message={t(formik.errors.user_agreed_term_and_condition || '')} />
						)}
				</div>

				{/* Submit Button */}
				<Button color="primary" variant="contained" fullWidth type="submit" loading={isLoading}>
					{t('singUp')}
				</Button>
			</form>
		</>
	);
}
